import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, Key, Sparkles } from "lucide-react";
import { getAssinaturasTicketMedio, putAssinaturaTicketMedio, getRoleFromToken } from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import { CartaoEquipamento } from "./PremissasEquipamentosView";

const ANO_ATUAL = new Date().getFullYear();
const OPCOES_ANO = Array.from({ length: ANO_ATUAL + 2 - 2025 + 1 }, (_, i) => 2025 + i);

const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  danger: "#EF4444",
  subcategoriaBg: isLight ? "#F5F0FC" : "#1E1435",
});

const headerBtnStyle = {
  display: "flex", alignItems: "center", gap: 6,
  background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
  borderRadius: 8, padding: "7px 12px", cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
};

// Premissas · Assinaturas e Sistemas: valor MENSAL por usuário travado por ANO, lista fixa —
// mesma mecânica de Premissas · Equipamentos e Materiais (de onde foi separada), catálogo em
// backend/services/premissas_equipamentos_svc.CATALOGO_ASSINATURAS. Consumida por "Lançar Kit
// Admissão" (Google obrigatória; Claude/Cursor/Gemini/GitLab opcionais), que lança em 2.6.11
// Assinaturas e Sistemas do mês da admissão até Dezembro — nunca em 2.7 Equipamentos e Materiais.
export default function PremissasAssinaturasView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();
  const isAdmin = getRoleFromToken() === "admin";

  const [ano, setAno] = useState(ANO_ATUAL);
  const [equipamentos, setEquipamentos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [salvandoCodigo, setSalvandoCodigo] = useState(null);

  useEffect(() => {
    setCarregando(true);
    setErro("");
    getAssinaturasTicketMedio(ano)
      .then(setEquipamentos)
      .catch((e) => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [ano]);

  async function salvarTicket(codigo, valor) {
    setSalvandoCodigo(codigo);
    setErro("");
    try {
      const atualizado = await putAssinaturaTicketMedio(codigo, Number(valor), ano);
      setEquipamentos((lista) => lista.map((e) => (e.codigo === codigo ? atualizado : e)));
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvandoCodigo(null);
    }
  }

  const sistemas = useMemo(() => equipamentos.filter((e) => e.grupo === "assinatura"), [equipamentos]);
  const ferramentasIa = useMemo(() => equipamentos.filter((e) => e.grupo === "assinatura_ia"), [equipamentos]);

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)", color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· Premissas · Assinaturas e Sistemas</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <select
              value={ano}
              onChange={(e) => setAno(parseInt(e.target.value, 10))}
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600 }}
            >
              {OPCOES_ANO.map((a) => <option key={a} value={a} style={{ color: "#0F172A" }}>{a}</option>)}
            </select>
            <button onClick={() => setIsLight((l) => !l)} style={headerBtnStyle}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />} {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button onClick={() => navigate("/inicio")} style={headerBtnStyle}>Menu Principal</button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: C.text }}>Premissas · Assinaturas e Sistemas</h1>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: C.muted }}>
              Valor mensal por usuário de cada assinatura, por ano — lista fixa, só o valor é editável. Um ano sem valor cadastrado usa o do ano mais próximo já cadastrado. No Kit Admissão, as assinaturas são lançadas em 2.6.11 Assinaturas e Sistemas, do mês da admissão até Dezembro.
              {!isAdmin && " Somente admin edita — os demais perfis visualizam."}
            </p>
          </div>

          {erro && <p style={{ margin: 0, fontSize: 12, color: C.danger }}>{erro}</p>}

          {carregando ? (
            <p style={{ fontSize: 12, color: C.muted }}>Carregando…</p>
          ) : (
            <>
              <section style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 18 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <Key size={15} color={C.brand} />
                  <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.text }}>Sistemas</h2>
                </div>
                <p style={{ margin: "0 0 14px", fontSize: 11, color: C.muted }}>
                  Google entra sempre em todo Kit Admissão; GitLab é opcional.
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
                  {sistemas.map((e) => (
                    <CartaoEquipamento
                      key={e.codigo} C={C} equipamento={e} isAdmin={isAdmin}
                      titulo={e.nome}
                      corDestaque={C.brand} corFundo={C.subcategoriaBg} corBorda={C.border}
                      salvando={salvandoCodigo === e.codigo}
                      onSalvar={(v) => salvarTicket(e.codigo, v)}
                    />
                  ))}
                </div>
              </section>

              <section style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 18 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <Sparkles size={15} color={C.brand} />
                  <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.text }}>Ferramentas de IA</h2>
                </div>
                <p style={{ margin: "0 0 14px", fontSize: 11, color: C.muted }}>
                  Opcionais no Kit Admissão — marque uma ou mais por kit.
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
                  {ferramentasIa.map((e) => (
                    <CartaoEquipamento
                      key={e.codigo} C={C} equipamento={e} isAdmin={isAdmin}
                      titulo={e.nome}
                      corDestaque={C.brand} corFundo={C.subcategoriaBg} corBorda={C.border}
                      salvando={salvandoCodigo === e.codigo}
                      onSalvar={(v) => salvarTicket(e.codigo, v)}
                    />
                  ))}
                </div>
              </section>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
